import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useProfile } from "../data/AppData";
import { familiarEmoji } from "../features/settings/familiars";
import { Icon, type IconName } from "./Icon";

const NAV: { to: string; label: string; icon: IconName }[] = [
  { to: "/", label: "Today", icon: "today" },
  { to: "/library", label: "Library", icon: "library" },
  { to: "/calendar", label: "Calendar", icon: "calendar" },
  { to: "/lessons", label: "Lessons", icon: "lessons" },
  { to: "/grimoire", label: "Grimoire", icon: "grimoire" },
];

export function Layout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const familiar = useProfile().data?.familiar;
  const isActive = (to: string) => (to === "/" ? path === "/" : path.startsWith(to));

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <Link to="/" className="wordmark" aria-label="Hexling, home">
          Hexling
        </Link>
        <Link
          to="/settings"
          className="avatar"
          aria-label={familiar ? `Settings and ${familiar.name}` : "Settings"}
        >
          <span aria-hidden="true">{familiar ? familiarEmoji(familiar.typeSlug) : "⚙️"}</span>
        </Link>
      </header>
      <nav className="tabbar" aria-label="Main">
        {NAV.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            className="tabbar__link"
            aria-current={isActive(n.to) ? "page" : undefined}
          >
            <Icon name={n.icon} />
            <span>{n.label}</span>
          </Link>
        ))}
      </nav>
      <main id="main" className="page" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

/** Consistent page heading with an optional back link. */
export function PageTitle({
  title,
  subtitle,
  back,
}: {
  title: string;
  subtitle?: React.ReactNode;
  back?: { to: string; label: string; params?: Record<string, string> };
}) {
  return (
    <div className="page-title">
      {back && (
        <Link to={back.to} params={back.params} className="back-link">
          <Icon name="back" size={18} />
          {back.label}
        </Link>
      )}
      <h1>{title}</h1>
      {subtitle && <p className="hx-muted page-title__sub">{subtitle}</p>}
    </div>
  );
}
